import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, forkJoin, of, Observable, Subject, takeUntil,shareReplay } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import {
  EvalSummaryDocumentModel,
  EvalSummaryResponse,
  ExtractionReportItem,
  ExtractionReportResponse,
} from '../../models/truth-management.model';
import { AuthService } from '../../services/auth.service';
import { ClientCapabilityService } from '../../services/client-capability.service';
import { GlobalSearchService } from '../../services/global-search.service';
import { ModalService } from '../../services/modal.service';
import { TruthManagement } from '../../services/truth-management';
import { PageHeaderComponent } from '../page-header/page-header.component';
import { SearchResultsDropdownComponent } from '../search-results-dropdown/search-results-dropdown.component';
import { SearchResultItem } from '../../models/global-search.model';

interface KpiState {
  modelsTrained: number;
  fieldsTrained: number;
  overallAccuracy: number;
  docsProcessedToday: number;
  pagesProcessedToday: number;
}

interface PieSlice {
  label: string;
  count: number;
  color: string;
  bgColor: string;
}

interface TrainingBar {
  label: string;
  count: number;
}

interface OperationalBar {
  label: string;
  count: number;
}

@Component({
  selector: 'app-dashboard-landing',
  standalone: true,
  imports: [CommonModule, PageHeaderComponent, SearchResultsDropdownComponent],
  templateUrl: './dashboard-landing.component.html',
  styleUrls: ['./dashboard-landing.component.scss'],
})
export class DashboardLandingComponent implements OnInit, OnDestroy {
  @ViewChild('inlineTooltip') inlineTooltipRef?: ElementRef<HTMLElement>;

  isAuthenticated = false;
  isRoleCheckInProgress = true;
  isLoadingDashboard = false;
  hasNoClientsAssigned = false;
  tooltipVisible = false;
  tooltipText = '';
  tooltipX = 0;
  tooltipY = 0;

  // Search functionality
  searchTerm$ = new BehaviorSubject<string>('');
  searchResults$: Observable<SearchResultItem[]> = of([]);
  isSearching = false;
  showSearchDropdown = false;

  readonly kpis: KpiState = {
    modelsTrained: 0,
    fieldsTrained: 0,
    overallAccuracy: 0,
    docsProcessedToday: 0,
    pagesProcessedToday: 0,
  };

  readonly pieSlices: PieSlice[] = [
    { label: 'Healthy', count: 0, color: '#065f46', bgColor: '#d1fae5' },
    { label: 'Field Missing', count: 0, color: '#92400e', bgColor: '#fef3c7' },
    { label: 'Field Update', count: 0, color: '#7c2d12', bgColor: '#fb923c' },
    { label: 'No Sample', count: 0, color: '#475569', bgColor: '#f1f5f9' },
  ];

  trainingBars: TrainingBar[] = [
    { label: '0%-40%', count: 0 },
    { label: '40%-70%', count: 0 },
    { label: '70%-80%', count: 0 },
    { label: '80%-90%', count: 0 },
    { label: '90%-99%', count: 0 },
    { label: '100%', count: 0 },
  ];

  operationalBars: OperationalBar[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    public authService: AuthService,
    public router: Router,
    private modalService: ModalService,
    private truthManagementService: TruthManagement,
    private globalSearchService: GlobalSearchService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private clientCapability: ClientCapabilityService,
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.authService.hasValidToken();

    this.authService.userRole$
      .pipe(takeUntil(this.destroy$))
      .subscribe((role) => {
        if (role !== null) {
          setTimeout(() => {
            this.isRoleCheckInProgress = false;
          }, 0);
        }
      });

    // Subscribe to client initialization and changes, reload dashboard on both
    this.authService.currentClient$
      .pipe(takeUntil(this.destroy$))
      .subscribe((client) => {
        if (client !== null) {
          setTimeout(() => {
            this.hasNoClientsAssigned = false;
            // Only load dashboard if role check has completed
            if (!this.isRoleCheckInProgress) {
              this.loadDashboard();
            }
          }, 0);
        } else if (!this.isRoleCheckInProgress) {
          // Only confirm no clients after role check completes
          setTimeout(() => {
            this.hasNoClientsAssigned = true;
          }, 0);
        }
      });

    // Restore the last search (term + results) if the user navigated away to a
    // search result and came back to /home, so it can be shown without a new
    // API call. Only used if the term hasn't changed since.
    const cachedSearch = this.globalSearchService.getCachedSearchResults();
    if (cachedSearch.term) {
      this.searchTerm$.next(cachedSearch.term);
      this.isSearching = true;
      this.showSearchDropdown = true;
    }

    // Setup global search with debouncing and client scoping
    this.searchResults$ = this.searchTerm$
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        switchMap((term) => {
          // Only search if term has 2+ characters
          if (term.length < 2) {
            this.showSearchDropdown = false;
            return of([]);
          }

          // Term matches the restored search - reuse cached results instead of refetching
          if (term === cachedSearch.term && cachedSearch.results.length > 0) {
            this.showSearchDropdown = true;
            return of(cachedSearch.results);
          }

         // Get current client ID from session storage
          const currentClientStr = sessionStorage.getItem('currentClient');
          if (!currentClientStr) {
            return of([]);
          }

          let clientId: string;
          try {
            const currentClientObj = JSON.parse(currentClientStr);
            clientId = currentClientObj.client_id;
            if (!clientId) {
              return of([]);
            }
          } catch (error) {
            return of([]);
          }

          this.isSearching = true;
          this.showSearchDropdown = true;
          return this.globalSearchService.searchGlobal(term, clientId).pipe(
            switchMap((response) => {
              this.globalSearchService.cacheSearchResults(term, response.results);
              return of(response.results);
            }),
            catchError((error) => {
              this.isSearching = false;
              return of([]);
            }),
          );
        }),
        shareReplay(1), 
        takeUntil(this.destroy$),
      );

    // Subscribe to search results to update loading state
    this.searchResults$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isSearching = false;
      this.cdr.detectChanges();
    });

    // Listen for clicks outside the search container to close dropdown
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('click', (event: MouseEvent) => {
        if (this.showSearchDropdown) {
          const clickedInside = this.elementRef.nativeElement.querySelector('.search-container')?.contains(event.target);
          if (!clickedInside) {
            this.ngZone.run(() => {
              this.onSearchDropdownClose();
            });
          }
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get totalPieCount(): number {
    return this.pieSlices.reduce((sum, s) => sum + s.count, 0);
  }

  get activePieSlices(): PieSlice[] {
    return this.pieSlices.filter((slice) => slice.count > 0);
  }

  get hasSingleActivePieSlice(): boolean {
    return this.activePieSlices.length === 1;
  }

  get singleActivePieSlice(): PieSlice | null {
    return this.activePieSlices[0] ?? null;
  }

  get trainingMax(): number {
    return Math.max(1, ...this.trainingBars.map((b) => b.count));
  }

  get operationalMax(): number {
    return Math.max(1, ...this.operationalBars.map((b) => b.count));
  }

  logout(): void {
    this.modalService.showLogoutConfirmation(() => {
      this.authService.logout();
      this.isAuthenticated = false;
    });
  }

  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  navigateToEvaluationDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  navigateToDataExtraction(): void {
    this.router.navigate(['/data_extraction']);
  }

  viewOperationalHealth(): void {
    this.clientCapability.navigateToOpsHealth();
  }

  getPieDashArray(count: number, index: number): string {
    if (this.totalPieCount <= 0) {
      return '0 100';
    }

    const percent = (count / this.totalPieCount) * 100;
    const overlap = 1.0;

    // For the closing slice, use remaining percent so floating-point rounding
    // does not leave a tiny seam at the wrap boundary (e.g., grey/green join).
    const isLastSlice = index === this.pieSlices.length - 1;
    if (isLastSlice) {
      const previousPercent = this.pieSlices
        .slice(0, index)
        .reduce((sum, s) => sum + (s.count / this.totalPieCount) * 100, 0);
      const remainder = Math.max(0, 100 - previousPercent);
      const overlapAdjustedRemainder = count > 0 ? Math.min(100, remainder + overlap) : 0;
      return `${overlapAdjustedRemainder} 100`;
    }

    const overlapAdjusted = count > 0 ? Math.min(100, percent + overlap) : 0;
    return `${overlapAdjusted} 100`;
  }

  getPieDashOffset(index: number): string {
    if (this.totalPieCount <= 0 || index <= 0) {
      return '0';
    }

    const prevPercent = this.pieSlices
      .slice(0, index)
      .reduce((sum, s) => sum + (s.count / this.totalPieCount) * 100, 0);

    return `-${prevPercent}`;
  }

  private loadDashboard(): void {
    const { startDateUtc, endDateUtc } = this.getUtcLast24HoursRange();
    this.isLoadingDashboard = true;

    // Get current clientId from AuthService
    const currentClient = this.authService.getCurrentClient();
    const clientId = currentClient?.client_id;
    forkJoin({
      // Force-refresh (bypass cache) so files_count/accuracy stay accurate after an upload,
      // ground-truth edit, or eval run made elsewhere — same fix as loan-validation.ts.
      summary: this.truthManagementService.refreshEvalSummary(clientId),
      extraction: this.truthManagementService.getExtractionReport(startDateUtc, endDateUtc, clientId).pipe(
        catchError(() => of<ExtractionReportResponse | null>(null)),
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ summary, extraction }) => {
          this.applyStateAndRefresh(() => {
            this.applySummary(summary);

            if (extraction) {
              this.applyExtractionMetrics(extraction.by_document_model || []);
            }

            this.isLoadingDashboard = false;
          });
        },
        error: () => {
          this.applyStateAndRefresh(() => {
            this.isLoadingDashboard = false;
            this.resetDashboard();
          });
        },
      });
  }

  private getUtcLast24HoursRange(): { startDateUtc: string; endDateUtc: string } {
    const now = new Date();
    const endDateUtc = new Date(now);
    endDateUtc.setUTCSeconds(0, 0);
    const startDateUtc = new Date(endDateUtc.getTime() - 24 * 60 * 60 * 1000);

    return {
      startDateUtc: startDateUtc.toISOString(),
      endDateUtc: endDateUtc.toISOString(),
    };
  }

  getPieSliceTooltip(slice: PieSlice): string {
    return `${slice.label}: ${slice.count}`;
  }

  getTrainingBarTooltip(bar: TrainingBar): string {
    const noun = bar.count === 1 ? 'Document Model' : 'Document Models';
    return `${bar.count} ${noun}`;
  }

  getOperationalBarTooltip(bar: OperationalBar): string {
    const noun = bar.count === 1 ? 'Request' : 'Requests';
    return `${bar.count} ${noun}`;
  }

  getOperationalBarHealthClass(_bar: OperationalBar): 'high' {
    return 'high';
  }

  onTooltipEnter(text: string, event: MouseEvent): void {
    this.tooltipText = text;
    this.tooltipVisible = true;

    const { clientX, clientY } = event;
    this.updateTooltipPosition(clientX, clientY);

    requestAnimationFrame(() => {
      if (this.tooltipVisible) {
        this.updateTooltipPosition(clientX, clientY);
      }
    });
  }

  onTooltipMove(event: MouseEvent): void {
    if (!this.tooltipVisible) {
      return;
    }

    this.updateTooltipPosition(event.clientX, event.clientY);
  }

  onTooltipLeave(): void {
    this.tooltipVisible = false;
  }

  onPieSliceEnter(slice: PieSlice, event: MouseEvent): void {
    this.onTooltipEnter(this.getPieSliceTooltip(slice), event);
  }

  onPieSliceMove(event: MouseEvent): void {
    this.onTooltipMove(event);
  }

  onPieSliceLeave(): void {
    this.onTooltipLeave();
  }

  onSearchInputChange(term: string): void {
    // Show loading immediately when user types (during debounce period)
    if (term.length > 0) {
      this.isSearching = true;
      this.showSearchDropdown = true;
    } else {
      this.isSearching = false;
      this.showSearchDropdown = false;
    }
    this.searchTerm$.next(term);
  }

  onSearchDropdownClose(): void {
    this.showSearchDropdown = false;
    this.searchTerm$.next('');
    this.globalSearchService.clearCachedSearchResults();
  }

  // Fired when a result is clicked (navigating away), as opposed to the
  // dropdown being dismissed - keep the cached search so it can be restored.
  onSearchResultSelected(): void {
    this.showSearchDropdown = false;
  }

  private applyStateAndRefresh(update: () => void): void {
    if (NgZone.isInAngularZone()) {
      update();
      this.cdr.detectChanges();
      return;
    }

    this.ngZone.run(() => {
      update();
      this.cdr.detectChanges();
    });
  }

  private updateTooltipPosition(clientX: number, clientY: number): void {
    const offset = 12;
    const minPadding = 8;
    const tooltipElement = this.inlineTooltipRef?.nativeElement;
    const tooltipRect = tooltipElement?.getBoundingClientRect();
    const tooltipWidth = tooltipRect?.width ?? 220;
    const tooltipHeight = tooltipRect?.height ?? 34;
    const maxLeft = Math.max(minPadding, window.innerWidth - tooltipWidth - minPadding);
    const maxTop = Math.max(minPadding, window.innerHeight - tooltipHeight - minPadding);

    this.tooltipX = Math.min(Math.max(clientX + offset, minPadding), maxLeft);
    this.tooltipY = Math.min(Math.max(clientY + offset, minPadding), maxTop);
  }

  private normalizeSummaryAccuracy(value: number): number {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return 0;
    }

    if (numericValue >= 0 && numericValue <= 1) {
      return Math.max(0, Math.min(100, numericValue * 100));
    }

    return Math.max(0, Math.min(100, numericValue));
  }

  private applySummary(summary: EvalSummaryResponse): void {
    const models = summary?.document_models || [];
    const trainingBarModels = models.filter((m) => m.accuracy !== null && m.accuracy !== undefined);

    this.kpis.modelsTrained = models.length;
    this.kpis.fieldsTrained = models.reduce((sum, m) => sum + (Number(m.attributes_count) || 0), 0);

    const normalizedAccuracies = models
      .filter((m) => m.accuracy !== null && m.accuracy !== undefined)
      .map((m) => this.normalizeSummaryAccuracy(m.accuracy))
      .filter((accuracy) => Number.isFinite(accuracy));

    this.kpis.overallAccuracy =
      normalizedAccuracies.length > 0
        ? normalizedAccuracies.reduce((sum, value) => sum + value, 0) / normalizedAccuracies.length
        : 0;

    this.kpis.docsProcessedToday = 0;
    this.kpis.pagesProcessedToday = 0;

    this.applyStatusPie(models);
    this.applyTrainingBars(trainingBarModels);
    this.operationalBars = [];
  }

  private applyExtractionMetrics(rows: ExtractionReportItem[]): void {
    this.kpis.docsProcessedToday = rows.reduce((sum, row) => sum + (Number(row.docs_processed) || 0), 0);

    this.kpis.pagesProcessedToday = rows.reduce((sum, row) => sum + (Number(row.pages_processed) || 0), 0);

    this.applyOperationalBarsFromExtraction(rows);
  }

  private applyStatusPie(models: EvalSummaryDocumentModel[]): void {
    const counts = {
      HEALTHY: 0,
      FIELD_MISSING: 0,
      FIELD_UPDATE: 0,
      NO_SAMPLE: 0,
    };

    models.forEach((model) => {
      const status = (model.status || '').toUpperCase();
      if (status === 'FIELD_MISSING') {
        counts.FIELD_MISSING++;
      } else if (status === 'FIELD_UPDATE') {
        counts.FIELD_UPDATE++;
      } else if (status === 'NO_SAMPLE') {
        counts.NO_SAMPLE++;
      } else {
        counts.HEALTHY++;
      }
    });

    this.pieSlices[0].count = counts.HEALTHY;
    this.pieSlices[1].count = counts.FIELD_MISSING;
    this.pieSlices[2].count = counts.FIELD_UPDATE;
    this.pieSlices[3].count = counts.NO_SAMPLE;
  }

  private applyTrainingBars(models: EvalSummaryDocumentModel[]): void {
    const accuracies = models.map((model) => this.normalizeSummaryAccuracy(model.accuracy));

    const bucketCounts = {
      '0%-40%': 0,
      '40%-70%': 0,
      '70%-80%': 0,
      '80%-90%': 0,
      '90%-99%': 0,
      '100%': 0,
    };

    accuracies.forEach((acc) => {
      if (acc >= 0 && acc < 40) bucketCounts['0%-40%']++;
      else if (acc >= 40 && acc < 70) bucketCounts['40%-70%']++;
      else if (acc >= 70 && acc < 80) bucketCounts['70%-80%']++;
      else if (acc >= 80 && acc < 90) bucketCounts['80%-90%']++;
      else if (acc >= 90 && acc < 100) bucketCounts['90%-99%']++;
      else if (acc === 100) bucketCounts['100%']++;
    });

    this.trainingBars = this.trainingBars.map((bar) => ({
      ...bar,
      count: bucketCounts[bar.label as keyof typeof bucketCounts] ?? 0,
    }));
  }

  private applyOperationalBarsFromExtraction(rows: ExtractionReportItem[]): void {
    this.operationalBars = rows
      .map((row) => ({
        label: row.document_model,
        count: Number(row.docs_processed) || 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }

  private resetDashboard(): void {
    this.kpis.modelsTrained = 0;
    this.kpis.fieldsTrained = 0;
    this.kpis.overallAccuracy = 0;
    this.kpis.docsProcessedToday = 0;
    this.kpis.pagesProcessedToday = 0;

    this.pieSlices.forEach((slice) => {
      slice.count = 0;
    });

    this.trainingBars = this.trainingBars.map((bar) => ({ ...bar, count: 0 }));
    this.operationalBars = [];
  }
}
